import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('obituaryDetails');
export interface ObituaryDetailsData {
  id?: string;
  // Obituary content fields
  obituaryPreference?: string;
  publicationNewspapers?: string;
  onlinePublications?: string;
  lengthPreference?: string;
  tonePreference?: string;
  keyAccomplishments?: string;
  familyMentions?: string;
  careerHighlights?: string;
  hobbiesInterests?: string;
  charitableWork?: string;
  militaryService?: string;
  educationBackground?: string;
  avoidTopics?: string;
  photoPreference?: string;
  serviceInformation?: string;
  donationRequests?: string;
  notes?: string;
  // Writer info fields (combining from obituary-writer-info entity)
  writerName?: string;
  relationship?: string;
  contactPhone?: string;
  contactEmail?: string;
  backupWriter?: string;
  backupContact?: string;
  writingStyle?: string;
  collaborationPreference?: string;
  deadlinePreference?: string;
  sourceMaterials?: string;
  interviewContacts?: string;
  specialInstructions?: string;
  writerNotes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateObituaryDetailsRequest {
  obituaryPreference?: string;
  publicationNewspapers?: string;
  onlinePublications?: string;
  lengthPreference?: string;
  tonePreference?: string;
  keyAccomplishments?: string;
  familyMentions?: string;
  careerHighlights?: string;
  hobbiesInterests?: string;
  charitableWork?: string;
  militaryService?: string;
  educationBackground?: string;
  avoidTopics?: string;
  photoPreference?: string;
  serviceInformation?: string;
  donationRequests?: string;
  notes?: string;
  writerName?: string;
  relationship?: string;
  contactPhone?: string;
  contactEmail?: string;
  backupWriter?: string;
  backupContact?: string;
  writingStyle?: string;
  collaborationPreference?: string;
  deadlinePreference?: string;
  sourceMaterials?: string;
  interviewContacts?: string;
  specialInstructions?: string;
  writerNotes?: string;
}

export interface UpdateObituaryDetailsRequest extends CreateObituaryDetailsRequest {}

export const obituaryDetailsService = {
  createObituaryDetails: async (data: CreateObituaryDetailsRequest): Promise<ObituaryDetailsData> => {
    try {
      logger.debug('[Service] Creating obituary details with data:', data);
      const response = await http.post('/api/obituary-details', data);
      logger.debug('[Service] Create response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error creating obituary details:', error);
      throw error;
    }
  },

  updateObituaryDetails: async (id: string, data: UpdateObituaryDetailsRequest): Promise<ObituaryDetailsData> => {
    try {
      logger.debug('[Service] Updating obituary details with ID:', id, 'data:', data);
      const response = await http.patch(`/api/obituary-details/${id}`, data);
      logger.debug('[Service] Update response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error updating obituary details:', error);
      throw error;
    }
  },

  getAllObituaryDetails: async (): Promise<ObituaryDetailsData[]> => {
    try {
      logger.debug('[Service] Getting all obituary details info');
      const response = await http.get('/api/obituary-details');
      logger.debug('[Service] Get all response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting obituary details info:', error);
      throw error;
    }
  },

  getObituaryDetailsById: async (id: string): Promise<ObituaryDetailsData> => {
    try {
      logger.debug('[Service] Getting obituary details by ID:', id);
      const response = await http.get(`/api/obituary-details/${id}`);
      logger.debug('[Service] Get by ID response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting obituary details by ID:', error);
      throw error;
    }
  },

  deleteObituaryDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Deleting obituary details with ID:', id);
      await http.delete(`/api/obituary-details/${id}`);
      logger.debug('[Service] Delete completed');
    } catch (error: any) {
      logger.error('[Service] Error deleting obituary details:', error);
      throw error;
    }
  },

  lockObituaryDetails: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Locking obituary details with ID:', id);
      await http.post(`/api/obituary-details/${id}/lock`);
      logger.debug('[Service] Lock completed');
    } catch (error: any) {
      logger.error('[Service] Error locking obituary details:', error);
      throw error;
    }
  },

  unlockObituaryDetails: async (id: string, granteeEmails: string[]): Promise<void> => {
    try {
      logger.debug('[Service] Unlocking obituary details with ID:', id, 'for emails:', granteeEmails);
      await http.post(`/api/obituary-details/${id}/unlock`, { granteeEmails });
      logger.debug('[Service] Unlock completed');
    } catch (error: any) {
      logger.error('[Service] Error unlocking obituary details:', error);
      throw error;
    }
  },

  getObituaryDetailsAccessGrants: async (id: string): Promise<any[]> => {
    try {
      logger.debug('[Service] Getting access grants for obituary details with ID:', id);
      const response = await http.get(`/api/obituary-details/${id}/access-grants`);
      logger.debug('[Service] Access grants response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting obituary details access grants:', error);
      throw error;
    }
  },
};